'use client';

/**
 * **The attribute treatment's group chrome, with one owner** (LF-6350 /
 * [ATTR-8], `docs/attribute-treatment.md` §1.10 — the 7a "Groups: rule" format;
 * widened to block-level composition by [INVDRAW-2] / VALE-73).
 *
 * A record split into named **sections**, each opened by a heading — a small
 * uppercase title with a hairline directly under it — and filled by one or more
 * **blocks**: a run of attribute rows, a full-width kept component, or (rarely) a
 * bare heading with no body of its own. A consumer hands over a flat list of
 * blocks and gets the finished section stack back.
 *
 * ## Why this exists
 *
 * It is the *bind to the source of truth* rule (root `CLAUDE.md`) applied to the
 * one part of this treatment that had no owner. [ATTR-3] promoted the geometry,
 * the motion and the row tokens; group chrome was left as **hand-typed classes at
 * the call site**, with only the title's type in a token — and that token had
 * itself drifted (`tracking-wider` against the prototype's `tracking-wide`, see
 * {@link ATTRIBUTE_GROUP_TITLE}). One consumer with hand-typed chrome is a
 * private decision; two would be a divergence, and the inventory drawer is the
 * second. So the divider, its padding, the space between sections, the
 * empty-section rule and the surface's selection colour all live here now, and a
 * new surface inherits them by rendering through this rather than by copying six
 * class strings and losing one.
 *
 * ## Block-level composition ([INVDRAW-2] / VALE-73)
 *
 * `AttributeGroupSpec`'s flat `{ title, fields }` could only ever express a run of
 * rows under one heading. The inventory drawer needs more: a section that opens
 * with a kept component and continues into rows, a full-width block that owns its
 * own layout, and a run of rows that wants one column rather than two — named as
 * **the substantial widening** in `components/attributes/CLAUDE.md` §9. Ported
 * from the settled prototype design, `InventoryBlock` in
 * `components/prototypes/inventory-drawer/inventory-attribute-specs.tsx`.
 *
 * A block's `title: null` **continues the section above it**: a self-labelling
 * kept component and the rows beneath it share one heading instead of inventing a
 * second. Blocks are grouped into sections at render time — a new section starts
 * at every block whose `title` is not `null` (or the very first block) — which is
 * what lets the empty-section rule below key off "does this section have any
 * visible content" rather than off array index.
 *
 * ## What it deliberately does not own
 *
 * **The tinted-surface variant.** `LeadDrawerCard` groups the enquiry drawer's
 * attributes with a card and rules *above* its heading, because that surface is
 * tinted and the card steps forward off it. This is the **white**-surface chrome
 * (the Contacts drawer, the inventory drawer): no card, a rule *under* the title.
 * The two share the title's type — {@link ATTRIBUTE_GROUP_TITLE}, which both now
 * bind to — and nothing else, which is exactly why the divider is not in that
 * token.
 */

import { cn } from '@/lib/utils';
import {
  ATTRIBUTE_COMPONENT_ONE_COLUMN,
  ATTRIBUTE_GROUP_TITLE,
  ATTRIBUTE_LABEL,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_ROW,
  ATTRIBUTE_SECTION_BODY_LEAD,
  ATTRIBUTE_SECTION_TITLE_RULE,
  ATTRIBUTE_SELECTION,
  attributeLabelWidthStyle
} from './attribute-tokens';
import type { BooleanControlSetting } from './attribute-boolean';
import { AttributeGrid } from './attribute-grid';
import type { AutoOpenSignal, AttributeSpec } from './attribute-types';

/**
 * A mark rendered immediately before a section's title, inside the same heading
 * (VALE-158).
 *
 * **Additive, and deliberately not a widening of `title` itself.** The obvious
 * move — letting `title` be a `ReactNode` — breaks two things that key off it
 * being a string: {@link toSections} groups on `title !== null`, and
 * {@link AttributeSectionsProps.fieldsTestIdPrefix} derives every grid's
 * `data-testid` by slugifying it. A separate slot keeps both intact, and a
 * section that sets no adornment renders byte-for-byte as it did before.
 *
 * The case it exists for is a section that belongs to an *external system*
 * rather than to the record: the Contacts drawer's ActionStep section is headed
 * by that provider's own brand mark, which names the whole concern once instead
 * of repeating it on the one row beneath. The mark itself always comes from
 * `lib/services/integrations/pms/provider-presentation.ts` — never a hand-rolled
 * label or icon.
 */
interface AttributeBlockTitleAdornment {
  /**
   * **A declared, stable render identity for this block** (VALE-1983),
   * mirroring {@link AttributeSpec.key} and existing for the same reason: a
   * block's *position* in the array is not a safe identity, because a caller
   * routinely spreads a block in conditionally (a market-data caption, an
   * institution search, `withoutInstructionBlocks`' lifecycle-gated
   * instruction pair) ahead of a `rows` block. The instant that condition
   * flips, every later block's array index shifts by one — and keying on the
   * index made React match the *same* `AttributeGrid` instance (with it, its
   * `openIndex`/open-editor `draft` state) to whatever block now sits at the
   * old index, silently transplanting an open editor onto an unrelated row.
   *
   * Most blocks never need this: the renderer falls back to a **content**-derived
   * identity (a `rows` block's own field keys/labels, a `component`/`heading`
   * block's title) that does not move when a sibling block appears or
   * disappears. Declare `key` only for a block whose content cannot be told
   * apart from a sibling's own fallback — an untitled `component` block with no
   * `label` is the one shape the fallback cannot distinguish from another like
   * it.
   */
  key?: string;
  /**
   * Rendered before the title text, in the section heading. Only read from the
   * block that *starts* a section (one whose `title` is not `null`); a
   * continuation block's adornment is ignored, since it renders no heading.
   */
  titleAdornment?: React.ReactNode;
  /**
   * Rendered at the **trailing edge** of the section heading, pushed to the far
   * right (VALE-587).
   *
   * Additive for the same reasons {@link AttributeBlockTitleAdornment} itself is
   * — a section that sets none renders byte-for-byte as it did before, because
   * the heading only becomes a spread flex row when one is supplied.
   *
   * **Distinct from `titleAdornment`, and the two are not interchangeable.** The
   * adornment is a *mark*: it qualifies the title and reads as part of it, so it
   * sits immediately before the words. This is an *action on the section as a
   * whole*, so putting it in the leading slot wedges a button between a mark and
   * the name it belongs to — which is exactly what it looked like before this
   * slot existed (the ActionStep mark, then a refresh icon, then the word
   * ActionStep, reading as two marks).
   *
   * **Not a way around §2.** This is section-level, so it stays one decision per
   * section rather than a per-field prop a call site remembers — the affordances
   * on a *row* are still derived from `editor.kind` and are not opt-in. Its case
   * is a section owned by an external system, where the action re-reads that
   * system rather than editing the record: the Contacts drawer's ActionStep
   * participant-type refresh.
   */
  titleAction?: React.ReactNode;
}

/**
 * A run of attribute rows — the block every existing consumer's `{ title, fields }`
 * shape mechanically becomes.
 *
 * `columns` overrides {@link AttributeSectionsProps.columns} for this block only:
 * two by default (the treatment's own density argument), one when the block's
 * labels need the room, or three when the block's values are short enough that
 * two columns reads as a band of air (VALE-129) — see {@link ATTRIBUTE_LABEL_WIDTH}
 * for all three columns' measured widths.
 */
export interface AttributeRowsBlock<
  TPatch
> extends AttributeBlockTitleAdornment {
  kind: 'rows';
  /** `null` continues the section above it. */
  title: string | null;
  fields: AttributeSpec<TPatch>[];
  columns?: 1 | 2 | 3;
}

/**
 * A kept component, full width, owning its own layout — for a capability the
 * treatment cannot yet express as rows. Not gated by `hidden`: an absent block is
 * how a caller conditions one, the same as it conditions which blocks it sends at
 * all.
 */
export interface AttributeComponentBlock extends AttributeBlockTitleAdornment {
  kind: 'component';
  /** `null` continues the section above it. */
  title: string | null;
  component: React.ReactNode;
  /**
   * **The diff keys this block stands for** (VALE-1597) — the block-level twin
   * of {@link AttributeSpec.diffKey}, and for the same reason.
   *
   * The AI-review treatment maps over a `rows` block's `fields`. A component
   * block has none: it owns its own layout and writes its own keys, so every
   * value it draws was invisible to every mark — no ink, no badge, no popover,
   * no reject, on updated and created items alike. That is why a new item in
   * the drawer did not read as wholly new; the treatment already answered
   * "new", it simply had nothing here to answer it about.
   *
   * The keys cannot be discovered the way a row's are, because a component
   * commits through its own callbacks rather than through a `toPatch` the
   * treatment can ask. So the block declares them, exactly as a kept picker
   * does, and the treatment wraps what the block drew.
   *
   * Absent is the correct answer for a block that draws no reviewable value —
   * an explanatory paragraph, a reconciliation hint — and such a block renders
   * byte for byte as before.
   */
  diffKeys?: readonly string[];
  /**
   * Renders the block **as a row**: the shared label column, then the component
   * in the value slot beside it (VALE-863).
   *
   * Without it a kept component starts at the container's left edge, ignoring
   * the label column every row above it observes — which is what made the share
   * registry picker read as a floating box rather than a field. A component
   * that is really one control wants to look like one.
   *
   * Implies the one-column cap, since a labelled row's value slot is that
   * column.
   */
  label?: string;
  /**
   * Caps the block at one grid column instead of the full width
   * ({@link ATTRIBUTE_COMPONENT_ONE_COLUMN}) — for a block that is a single
   * control rather than its own layout.
   *
   * Opt-in, and deliberately not the default: most component blocks own a whole
   * arrangement (a price comparison, an instruction editor) that a half-width
   * cap would break.
   */
  oneColumn?: boolean;
}

/**
 * A section heading with no rows of its own, for a section that opens with a
 * kept component rather than a run of rows.
 */
export interface AttributeHeadingBlock extends AttributeBlockTitleAdornment {
  kind: 'heading';
  title: string;
}

/**
 * One block of the record, in reading order. A *different axis* from
 * {@link AttributeEditor} — `component` / `heading` / `rows` are record
 * **composition**, not editor kinds, and must never be expressed as an
 * `AttributeEditor` arm (`components/attributes/CLAUDE.md` §9).
 */
export type AttributeBlock<TPatch> =
  | AttributeRowsBlock<TPatch>
  | AttributeComponentBlock
  | AttributeHeadingBlock;

export interface AttributeSectionsProps<TPatch> {
  blocks: AttributeBlock<TPatch>[];
  /** Commits one field's patch. **Must reject to surface a failure** (§2). */
  onSave: (patch: TPatch) => Promise<unknown>;
  /** Fires as a field opens and closes, so a drawer can keep `Escape`. */
  onEditingChange?: (editing: boolean) => void;
  /**
   * The default for a `rows` block that does not set its own `columns`. Two for
   * a drawer, one for a rail, three for a full-width surface with short values
   * (VALE-129).
   */
  columns?: 1 | 2 | 3;
  /** Passed through to every `rows` block's grid — see {@link BooleanControlSetting}. */
  booleanControl?: BooleanControlSetting;
  /**
   * Passed through to every `rows` block's grid — the temporary hold on the
   * pre-unification chrome (LF-6388 / [ATTR-10]). Defaults to `false`, i.e. the
   * unified treatment; see {@link AttributeGrid}'s own prop for why a new
   * surface must never reach for it.
   */
  legacyChrome?: boolean;
  /** Passed through to every `rows` block's grid — see `AttributeGrid`'s own prop. */
  autoOpen?: AutoOpenSignal | null;
  /** `data-testid` for the section stack itself. */
  testId?: string;
  /**
   * Prefix for each `rows` block's grid `data-testid`, suffixed with the
   * slugified section title (`contacts` → `contact-attribute-fields-personal-details`).
   * A second (or later) `rows` block continuing the same section gets its own
   * block index appended, so only the first `rows` block of a section keeps the
   * exact id an existing consumer already asserts on.
   */
  fieldsTestIdPrefix?: string;
}

const slug = (title: string) =>
  title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/**
 * A block does not carry "is this hidden" itself — a `component` block is always
 * present when the caller sends it, and a bare `heading` block has no body of its
 * own to show. Only a `rows` block can be entirely gated off.
 */
const blockHasVisibleContent = <TPatch,>(block: AttributeBlock<TPatch>) => {
  if (block.kind === 'component') return true;
  if (block.kind === 'heading') return false;
  return block.fields.some((field) => !field.hidden);
};

/**
 * **A block's render identity — content-derived, never the array position it
 * happens to occupy this render** (VALE-1983). See {@link
 * AttributeBlockTitleAdornment.key}'s doc for why position is unsafe: a `rows`
 * block's identity is the field keys/labels it carries, which travel with the
 * block wherever it renders, so a conditionally-spread sibling appearing or
 * disappearing ahead of it never changes what this returns — and so never
 * causes React to hand this block's `AttributeGrid` instance, and the open
 * editor state inside it, to a *different* block that happened to land at the
 * same index. A `component`/`heading` block has no fields, so it falls back to
 * its title (and a labelled `component`'s label) — an untitled, unlabelled
 * `component` block has nothing left to key on and should declare `key`.
 */
const blockIdentity = <TPatch,>(block: AttributeBlock<TPatch>): string => {
  if (block.key) return `keyed:${block.key}`;
  if (block.kind === 'heading') return `heading:${block.title}`;
  if (block.kind === 'component') {
    return `component:${block.title ?? ''}:${block.label ?? ''}`;
  }
  return `rows:${block.title ?? ''}:${block.fields
    .map((field) => field.key ?? field.label)
    .join('|')}`;
};

/**
 * Groups a flat block list into sections — a new section starts at every block
 * whose `title` is not `null`, or at the very first block regardless. Each
 * section's own blocks keep their original relative order and index, which is
 * what lets `fieldsTestIdPrefix` below tell "the section's first `rows` block"
 * (no suffix, matching the pre-widening id) from a continuation.
 */
const toSections = <TPatch,>(blocks: AttributeBlock<TPatch>[]) => {
  const sections: {
    title: string | null;
    titleAdornment?: React.ReactNode;
    titleAction?: React.ReactNode;
    blocks: AttributeBlock<TPatch>[];
  }[] = [];
  for (const block of blocks) {
    const startsNewSection = block.title !== null || sections.length === 0;
    if (startsNewSection) {
      // The adornment is taken from the section-*starting* block only — a
      // continuation (`title: null`) renders no heading, so it has nowhere to
      // put one. See {@link AttributeBlockTitleAdornment}.
      sections.push({
        title: block.title,
        titleAdornment: block.titleAdornment,
        titleAction: block.titleAction,
        blocks: [block]
      });
    } else {
      sections[sections.length - 1].blocks.push(block);
    }
  }
  return sections;
};

export function AttributeSections<TPatch>({
  blocks,
  onSave,
  onEditingChange,
  columns = 2,
  booleanControl,
  legacyChrome,
  autoOpen,
  testId,
  fieldsTestIdPrefix
}: AttributeSectionsProps<TPatch>) {
  const sections = toSections(blocks);

  return (
    /*
      **`gap-6` on the stack rather than `mt-6` on every section but the first.**
      The prototype writes the space as `mt-6` on `!first`, and the two measure
      identically (20.4px at this theme's 3.4px `--spacing`) — but `first` there
      means *array index 0*, and here a section can drop out entirely (the
      empty-section rule below). Keying the space to an index would then put a
      leading gap above whichever section happened to render first, so the space
      belongs to the container, which simply never lays out a `null` child.

      The selection colour rides along here because this is the outermost element
      the treatment owns on the surface — see {@link ATTRIBUTE_SELECTION}.
    */
    <div
      className={cn('flex flex-col gap-6', ATTRIBUTE_SELECTION)}
      data-testid={testId}
    >
      {sections.map((section, sectionIndex) => {
        /*
          **A section whose every block has no visible content renders nothing at
          all — heading included.** Moved here from `contact-attribute-grid.tsx`
          with the rest of the chrome, since a heading over an empty space is a
          property of the chrome rather than of any one surface. Contacts' live
          case is `Notes`: both its rows are `hidden` for a global contact without
          the `contacts-page` flag, and a bare `NOTES` heading over nothing reads
          as content that failed to load rather than as a section that does not
          apply. Generalised from the single-`rows`-block "group" case to any mix
          of blocks a section now carries.

          Note this is the *same* `hidden` predicate the grid filters on, not a
          second reading of it — the grid drops gated rows from the layout, the
          keyboard chain and the a11y tree (see its `visible` derivation).
        */
        if (!section.blocks.some((block) => blockHasVisibleContent(block)))
          return null;

        let rowsBlockIndex = 0;

        return (
          // eslint-disable-next-line @eslint-react/no-array-index-key -- sections are a derived grouping recomputed fresh from `blocks` every render, not a reorderable/filterable collection, and a section has no caller-provided id (an untitled or duplicate-titled section is legitimate)
          <section key={`${section.title ?? 'untitled'}-${sectionIndex}`}>
            {/*
              Rule *under* the title: the white-surface half of §1.10's
              surface-dependent group chrome, now read from
              {@link ATTRIBUTE_SECTION_TITLE_RULE} and
              {@link ATTRIBUTE_SECTION_BODY_LEAD} rather than typed here — the
              shipped Contacts drawer measured `pb-1.5` (5.1px against the
              reference's 6.8px), which is the kind of one-step drift that
              hand-typed chrome accumulates and nothing notices (VALE-1689).
            */}
            {section.title !== null ? (
              <h3
                className={cn(
                  ATTRIBUTE_GROUP_TITLE,
                  ATTRIBUTE_SECTION_TITLE_RULE
                )}
              >
                {/*
                  An un-adorned section renders the bare string exactly as it
                  always has — the flex wrapper only appears when a mark or an
                  action is actually supplied, so no existing heading moves by a
                  pixel.

                  `justify-between` only when there is a trailing action to push
                  away; a heading with a mark alone keeps its old left-hugging
                  layout rather than stretching the title across the row.
                */}
                {section.titleAdornment || section.titleAction ? (
                  <span
                    className={cn(
                      'flex items-center gap-1.5',
                      section.titleAction && 'justify-between'
                    )}
                  >
                    <span className="flex items-center gap-1.5">
                      {section.titleAdornment}
                      {section.title}
                    </span>
                    {section.titleAction}
                  </span>
                ) : (
                  section.title
                )}
              </h3>
            ) : null}
            {section.blocks.map((block, blockIndex) => {
              // A bare `heading` block supplies the section's title only — it has
              // no body of its own to render.
              if (block.kind === 'heading') return null;

              /*
                **Only the block that opens a section is padded. Every other
                block in it adds nothing** (VALE-1689).

                Every block used to carry {@link ATTRIBUTE_SECTION_BODY_LEAD},
                which made the gap between two fields depend on where a block
                boundary happened to fall: 3.4px inside a block
                ({@link ATTRIBUTE_ROW}'s `py-0.5` on each side) against 10.2px
                across one. But a block is a *layout* grouping — a differing
                column count, a logical cluster in a spec — and none of that is
                visible on screen, so the wider gap marked a break the reader
                could not see, and the list's rhythm changed partway down for no
                reason they could name.

                **No block kind is exempt.** A caption, a kept component and a
                run of rows are all just content inside the section as far as
                the reader is concerned, so they sit on the one rhythm. The only
                break that reads as a break is a titled section, and that space
                never came from here: it is the section stack's `gap-6` plus the
                heading's own rule, both untouched.

                `isSectionLead` is keyed to the first block that actually PUTS
                SOMETHING ON SCREEN, not to `blockIndex === 0`: a bare `heading`
                block renders null and a `rows` block whose every field is
                `hidden` renders an empty one, so either can sit in front of the
                block the space under the rule really belongs to. Padding an
                invisible block would put real space on screen for something
                that is not there. `blockHasVisibleContent` is the same predicate
                the empty-section rule above uses rather than a second reading
                of it.
              */
              const isSectionLead =
                section.blocks.findIndex((candidate) =>
                  blockHasVisibleContent(candidate)
                ) === blockIndex;
              const lead = isSectionLead ? ATTRIBUTE_SECTION_BODY_LEAD : null;

              const key = blockIdentity(block);
              if (block.kind === 'component') {
                if (block.label) {
                  return (
                    <div
                      key={key}
                      className={cn('@container', ATTRIBUTE_ROW, lead)}
                      /*
                        **Both halves of the grid's own label sizing** (VALE-863),
                        because `ATTRIBUTE_LABEL_WIDTH` is a *container query*.

                        `attributeLabelWidthStyle` publishes `--attr-label-w`,
                        without which `w-[var(--attr-label-w)]` is invalid and the
                        label silently collapses to its own text — that is what put
                        the share registry picker on its own left edge. And
                        `@container` is what the published width then *resolves
                        against*: `AttributeGrid` declares one on itself, so a row
                        outside the grid measures some wider ancestor instead and
                        lands on a third edge again.

                        Declared here, on the full-width row wrapper, it matches the
                        grid's own container exactly. Note the trap
                        `SrnShareSearchField` records: declaring `@container` on a
                        narrow *cell* makes the column a fraction of every other
                        row's. The wrapper is the right element; the cell is not.
                      */
                      style={attributeLabelWidthStyle(columns)}
                    >
                      <span
                        className={cn(ATTRIBUTE_LABEL, ATTRIBUTE_LEADING)}
                        title={block.label}
                      >
                        {block.label}
                      </span>
                      <div
                        className={cn(
                          'min-w-0 flex-1',
                          ATTRIBUTE_COMPONENT_ONE_COLUMN
                        )}
                      >
                        {block.component}
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={key}
                    className={cn(
                      'w-full',
                      lead,
                      block.oneColumn && ATTRIBUTE_COMPONENT_ONE_COLUMN
                    )}
                  >
                    {block.component}
                  </div>
                );
              }

              // block.kind === 'rows'
              const isFirstRowsBlock = rowsBlockIndex === 0;
              rowsBlockIndex += 1;
              return (
                <div key={key} className={lead ?? undefined}>
                  <AttributeGrid<TPatch>
                    fields={block.fields}
                    columns={block.columns ?? columns}
                    booleanControl={booleanControl}
                    legacyChrome={legacyChrome}
                    onSave={onSave}
                    onEditingChange={onEditingChange}
                    autoOpen={autoOpen}
                    testId={
                      fieldsTestIdPrefix && section.title !== null
                        ? `${fieldsTestIdPrefix}-${slug(section.title)}${
                            isFirstRowsBlock ? '' : `-${rowsBlockIndex}`
                          }`
                        : undefined
                    }
                  />
                </div>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}
